/* ScanPreview.jsx — Gratis zonnecheck resultaten + upsell naar volledig persoonlijk plan
   Route: /scan?token=...
   i18n: alle strings via `scan_preview.*` namespace (7 talen).
*/

import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trans, useTranslation } from "react-i18next";
import {
  ArrowRight, Check, Loader2, TrendingDown, Banknote, Sun, Home as HomeIcon, Map,
  ShieldCheck, Sparkles, FileSearch, Wallet, PiggyBank, MessageCircle, Lock, Star,
} from "lucide-react";
import { apiClient } from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function ScanPreview() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();

  const [lead, setLead] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { nav("/"); return; }
    (async () => {
      try {
        const leadR = await apiClient.get("/leads/me", { params: { token } });
        const ld = leadR.data;
        if (ld.funnel === "v3" && !ld.intake?.scan) {
          nav(`/scan-intake?token=${token}`); return;
        }
        if (!ld.monthly_bill_eur || ld.monthly_bill_eur <= 0) {
          nav(`/scan-intake?token=${token}`); return;
        }
        setLead(ld);
        const previewR = await apiClient.post("/calc/preview", {
          region: ld.region, monthly_bill_eur: ld.monthly_bill_eur,
        });
        setPreview(previewR.data);
      } catch {
        toast.error(t("scan_preview.load_error"));
        setTimeout(() => nav("/"), 1500);
      } finally { setLoading(false); }
    })();
  }, [token, nav, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#FCBF49] mx-auto mb-4 animate-spin" />
          <p className="text-[#52606E]">{t("scan_preview.loading")}</p>
        </div>
      </div>
    );
  }

  if (!lead || !preview) return null;

  return (
    <div className="min-h-screen bg-[#F4F7FA] text-[#003049] antialiased font-sans">
      <MiniHeader />
      <ResultsHero lead={lead} preview={preview} />
      <InsightsGrid lead={lead} preview={preview} />
      <Upsell token={token} />
      <SmallFaq />
    </div>
  );
}

function MiniHeader() {
  const { t } = useTranslation();
  return (
    <header className="bg-white border-b border-[#E5E9EE] sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 h-[64px] flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src="/img/doctorsol-logo.svg" alt="DoctorSol" className="h-12 w-auto" draggable={false} />
        </Link>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-[#1F7A4B] bg-[#1F7A4B]/10 rounded-full px-3 py-1.5">
            <Check className="w-3.5 h-3.5" strokeWidth={3} />
            {t("scan_preview.header_badge")}
          </div>
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}

function ResultsHero({ lead, preview }) {
  const { t } = useTranslation();
  const firstName = (lead.name || "").split(" ")[0] || "";
  const scan = lead.intake?.scan || {};
  const housingLabel = t(`scan_preview.housing.${scan.housing_type}`, { defaultValue: t("scan_preview.housing.default") });
  const greeting = firstName
    ? t("scan_preview.hero_greeting_named", { name: firstName })
    : t("scan_preview.hero_greeting_anon");
  const personsWord = Number(scan.persons) === 1 ? t("scan_preview.person_one") : t("scan_preview.person_other");
  const summaryKey = scan.m2 && scan.persons ? "scan_preview.hero_summary_full" : "scan_preview.hero_summary_short";

  return (
    <section className="py-12 lg:py-16 bg-gradient-to-br from-[#003049] to-[#001D2B] text-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-xs uppercase tracking-[0.22em] text-[#FCBF49] font-bold mb-3">{t("scan_preview.hero_kicker")}</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">{greeting}</h1>
        <p className="text-white/80 text-lg max-w-2xl leading-relaxed">
          <Trans
            i18nKey={summaryKey}
            values={{
              housing: housingLabel, m2: scan.m2 || "", persons: scan.persons || "", persons_word: personsWord,
              region: preview.region, bill: Math.round(lead.monthly_bill_eur),
            }}
            components={{ strong: <span className="font-semibold text-white" /> }}
          />
        </p>
      </div>
    </section>
  );
}

function InsightsGrid({ lead, preview }) {
  const { t } = useTranslation();
  const annualKwh = Math.round((lead.monthly_bill_eur * 12) / 0.27);
  const panels = Math.max(4, Math.ceil(preview.system_kwp / 0.5));
  const priceLow = Math.round(preview.gross_cost_eur * 0.85);
  const priceHigh = Math.round(preview.gross_cost_eur * 1.15);

  const fitKey = preview.system_kwp >= 5 ? "fit_high" : preview.system_kwp >= 3 ? "fit_med" : "fit_low";
  const fitColor = preview.system_kwp >= 5 ? "#1F7A4B" : preview.system_kwp >= 3 ? "#FCBF49" : "#D62828";

  const fmtNL = (n) => Number(n).toLocaleString("nl-NL");
  const cards = [
    { n: 1, Icon: TrendingDown, key: "global",
      big: t("scan_preview.insight.global_big", { kwh: fmtNL(annualKwh) }),
      sub: t("scan_preview.insight.global_sub", { bill: Math.round(lead.monthly_bill_eur), region: preview.region }) },
    { n: 2, Icon: Banknote, key: "savings", highlight: true,
      big: t("scan_preview.insight.savings_big", { eur: fmtNL(preview.annual_savings_eur) }),
      sub: t("scan_preview.insight.savings_sub", { years: preview.payback_years }) },
    { n: 3, Icon: Sun, key: "first_scenario",
      big: t("scan_preview.insight.first_scenario_big", { kwp: preview.system_kwp, panels }),
      sub: t("scan_preview.insight.first_scenario_sub", { kwp: preview.system_kwp }) },
    { n: 4, Icon: HomeIcon, key: "roof_fit",
      big: <span style={{ color: fitColor }}>{t(`scan_preview.insight.${fitKey}`)}</span>,
      sub: t("scan_preview.insight.roof_sub") },
    { n: 5, Icon: Map, key: "price",
      big: t("scan_preview.insight.price_big", { low: fmtNL(priceLow), high: fmtNL(priceHigh) }),
      sub: t("scan_preview.insight.price_sub", { region: preview.region }) },
  ];

  return (
    <section className="py-12 lg:py-16">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((c) => (
            <div key={c.n} data-testid={`scan-insight-${c.n}`}
              className={`relative rounded-2xl p-6 border ${c.highlight ? "bg-[#FFFBEF] border-[#FCBF49]" : "bg-white border-[#EFF2F5]"}`}>
              <div className="absolute -top-3 -right-3 bg-[#FCBF49] text-[#003049] font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shadow">{c.n}</div>
              <c.Icon className="w-7 h-7 text-[#FCBF49] mb-4" />
              <div className="text-xs uppercase tracking-wider font-semibold text-[#52606E] mb-1">
                {t(`scan_preview.insight.${c.key}`)}
              </div>
              <div className="text-2xl font-bold text-[#003049] mb-3 leading-tight">{c.big}</div>
              <p className="text-sm text-[#52606E] leading-relaxed">{c.sub}</p>
            </div>
          ))}
          <a href="#upsell" className="hidden lg:flex bg-[#003049] text-white rounded-2xl p-6 flex-col justify-center hover:bg-[#001D2B] transition group cursor-pointer no-underline" data-testid="scan-lock-card">
            <Lock className="w-7 h-7 text-[#FCBF49] mb-4" />
            <h3 className="font-bold text-lg mb-2 leading-tight">{t("scan_preview.lock_title")}</h3>
            <p className="text-sm text-white/80 leading-relaxed mb-4">{t("scan_preview.lock_body")}</p>
            <span className="text-[#FCBF49] font-semibold inline-flex items-center gap-1.5 text-sm group-hover:gap-2.5 transition-all">
              {t("scan_preview.lock_link")} <ArrowRight className="w-4 h-4" />
            </span>
          </a>
        </div>
        <p className="text-center text-xs text-[#52606E] mt-7 max-w-2xl mx-auto">
          {t("scan_preview.indicative_note")}
        </p>
      </div>
    </section>
  );
}

function Upsell({ token }) {
  const { t } = useTranslation();
  const benefits = t("scan_preview.benefits", { returnObjects: true }) || [];
  const ICONS = [FileSearch, Banknote, Wallet, MessageCircle];
  const trust = t("scan_preview.trust", { returnObjects: true }) || [];

  return (
    <section id="upsell" className="py-16 lg:py-24 bg-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 bg-[#FCBF49]/15 rounded-full px-4 py-1.5 mb-5">
            <Sparkles className="w-4 h-4 text-[#FCBF49]" />
            <span className="text-xs font-bold text-[#003049] uppercase tracking-wider">{t("scan_preview.upsell_badge")}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#003049] leading-tight">{t("scan_preview.upsell_title")}</h2>
          <p className="mt-5 text-[#52606E] text-lg leading-relaxed">{t("scan_preview.upsell_sub")}</p>
        </div>

        <div className="bg-gradient-to-br from-[#003049] to-[#001D2B] rounded-3xl p-8 lg:p-12 text-white shadow-2xl">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.22em] text-[#FCBF49] font-bold mb-4">{t("scan_preview.benefits_kicker")}</div>
              <ul className="space-y-4">
                {benefits.map((b, i) => {
                  const Icon = ICONS[i] || FileSearch;
                  return (
                    <li key={i} className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#FCBF49]/20 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-[#FCBF49]" />
                      </div>
                      <div>
                        <div className="font-bold mb-0.5">{b.t}</div>
                        <p className="text-sm text-white/75 leading-relaxed">{b.d}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-white text-[#003049] rounded-2xl p-7 shadow-xl">
                <div className="text-center">
                  <div className="text-xs uppercase tracking-wider text-[#52606E] mb-2 font-semibold">{t("scan_preview.price_label")}</div>
                  <div className="flex items-baseline justify-center gap-3 mb-1">
                    <span className="text-[#52606E] text-2xl line-through font-medium">€ 149</span>
                    <span className="text-5xl font-bold text-[#003049]">€ 49</span>
                  </div>
                  <div className="text-sm text-[#52606E] mb-5">{t("scan_preview.price_disclaimer")}</div>
                  <div className="inline-flex items-center gap-1.5 bg-[#1F7A4B]/10 text-[#1F7A4B] rounded-full px-3 py-1 text-xs font-bold mb-6">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} /> {t("scan_preview.price_save")}
                  </div>
                  <Link to={`/portal?token=${token}`} data-testid="scan-upsell-cta"
                    className="block w-full bg-[#FCBF49] hover:bg-[#E5AB2F] text-[#003049] font-bold rounded-md py-4 transition flex items-center justify-center gap-2">
                    {t("scan_preview.cta")} <ArrowRight className="w-4 h-4" />
                  </Link>
                  <p className="text-xs text-[#52606E] mt-4 text-center">{t("scan_preview.payment_note")}</p>
                </div>
              </div>

              <div className="mt-5 bg-white/5 rounded-xl p-4 flex items-center gap-3 backdrop-blur border border-white/10">
                <div className="flex gap-0.5">{[1,2,3,4,5].map(s=><Star key={s} className="w-3.5 h-3.5 text-[#FCBF49] fill-[#FCBF49]" />)}</div>
                <div className="text-xs text-white/80">{t("scan_preview.social_proof")}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-x-7 gap-y-3 text-sm text-[#52606E]">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#1F7A4B]" /> {trust[0]}</span>
          <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#1F7A4B]" strokeWidth={3} /> {trust[1]}</span>
          <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#1F7A4B]" strokeWidth={3} /> {trust[2]}</span>
        </div>
      </div>
    </section>
  );
}

function SmallFaq() {
  const { t } = useTranslation();
  return (
    <section className="py-16 bg-[#F7F8FA] border-t border-[#EFF2F5]">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <h2 className="text-2xl font-bold text-[#003049] mb-3">{t("scan_preview.faq_title")}</h2>
        <p className="text-[#52606E] leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: t("scan_preview.faq_body") }} />
        <PiggyBank className="w-10 h-10 text-[#FCBF49] mx-auto mb-2" />
        <p className="text-xs text-[#52606E] max-w-md mx-auto">{t("scan_preview.faq_footer")}</p>
      </div>
    </section>
  );
}
